import { ThreeEvent } from '@react-three/fiber';
import React, { memo, useCallback, useMemo, useState } from 'react';
import * as THREE from 'three';

import { DraggablePointProps } from '../../@types/Components';
import { EInteractionMode } from '../../@types/Enums';
import { useDraggingContext } from '../../provider/DraggingProvider';
import { useToolbarContext } from '../../provider/ToolbarProvider';
import { useZoomChangeContext } from '../../provider/ZoomChangeProvider';
import { SPHERE_GEOMETRY_X, SPHERE_GEOMETRY_YZ } from '../../utils/constants';
// DraggablePoint component: Represents a draggable point on the wall (either start or end).
const DraggablePoint = memo(({ position, pointType, onStartDragging, allowPropagation }: DraggablePointProps): React.JSX.Element => {
    const [isVisible, setIsVisible] = useState(false);  // State to control visibility
    const { isDragging, setIsDragging } = useDraggingContext();
    const threePosition = useMemo(() => new THREE.Vector3(...position), [position]);
    const { interactionMode } = useToolbarContext();
    const { staggeredZoomData } = useZoomChangeContext();
    const sizeValue = useMemo(
        () => staggeredZoomData <= 15 ? SPHERE_GEOMETRY_X - ((staggeredZoomData * .1) - 1.5) : SPHERE_GEOMETRY_X,
        [staggeredZoomData]
    );
    const handlePointerDown = (e: ThreeEvent<PointerEvent>): void => {
        if(e.target !== undefined) {
            if(!allowPropagation) {
                e.stopPropagation();
            }
            if(interactionMode !== EInteractionMode.MOVE) {
                onStartDragging(pointType); // Sets the draggingPosition in Wall component.
                setIsDragging(true);
                setIsVisible(true);
            }
        }
    };

    const handlePointerUp = useCallback(() => {
        if (isDragging) {
            setIsDragging(false);
            setIsVisible(false);
        }
    }, [isDragging, setIsDragging]);

    return (
        <group
            name={`group-dragPoint-${pointType}`}
            position={threePosition}
        >
            {/* Outline Sphere */}
            <mesh
                name={"dragPoint-outlineSphere-mesh"}
                visible={isVisible}
                scale={1.4}
            >
                <sphereGeometry
                    attach="geometry"
                    args={[sizeValue, SPHERE_GEOMETRY_YZ, SPHERE_GEOMETRY_YZ]}
                    name={"dragPoint-outlineSphere-mesh-sphereGeometry"}
                />
                <meshBasicMaterial
                    attach="material"
                    color="#E9B300"
                    name={"dragPoint-outlineSphere-mesh-meshBasicMaterial"}
                />
            </mesh>
            {/* Main Sphere */}
            <mesh
                name={"dragPoint-mainSphere-mesh"}
                position={[0, 0, 1]} // Slightly ontop of the outline
                visible={isVisible}
                onPointerEnter={() => {
                    if(interactionMode !== EInteractionMode.MOVE) {
                        setIsVisible(true);
                    }
                }}
                onPointerLeave={() => setIsVisible(false)}
                onPointerDown={(e) => {
                    if (e.button !== 0) return; 
                    handlePointerDown(e);
                }}       
                onPointerUp={handlePointerUp}
            >
                <sphereGeometry
                    attach="geometry"
                    args={[sizeValue, SPHERE_GEOMETRY_YZ, SPHERE_GEOMETRY_YZ]}
                    name={"dragPoint-mainSphere-mesh-sphereGeometry"}
                />
                <meshBasicMaterial
                    attach="material"
                    color="#F4D980"
                    name={"dragPoint-mainSphere-mesh-meshBasicMaterial"}
                />
            </mesh>
        </group>
    );
});

export default DraggablePoint;
